// frontend/src/pages/SetupPage.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  Alert,
  useTheme,
  alpha,
  CircularProgress,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import PageTransition from '../components/animations/PageTransition';

// Icons
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

const SetupPage = () => {
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token') || '';

  const [setupToken, setSetupToken] = useState(tokenFromUrl);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [setupRequired, setSetupRequired] = useState(false);
  const [hasValidToken, setHasValidToken] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  // Check setup status on mount
  useEffect(() => {
    const checkSetupStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/setup/status`);
        if (response.ok) {
          const data = await response.json();
          setSetupRequired(data.setup_required);
          setHasValidToken(data.has_valid_token);

          // If setup is not required, redirect to login
          if (!data.setup_required) {
            navigate('/login');
          }
        }
      } catch (err) {
        setError('Could not check setup status. Please try again.');
      } finally {
        setCheckingStatus(false);
      }
    };

    // If already logged in, redirect to dashboard
    if (currentUser) {
      navigate('/dashboard');
      return;
    }

    checkSetupStatus();
  }, [currentUser, navigate]);

  const validateForm = () => {
    if (!setupToken.trim()) {
      setError('Setup token is required');
      return false;
    }
    if (!username.trim()) {
      setError('Username is required');
      return false;
    }
    if (username.length < 3) {
      setError('Username must be at least 3 characters');
      return false;
    }
    if (!email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!password) {
      setError('Password is required');
      return false;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: setupToken,
          username,
          email,
          password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Setup failed');
      }

      // Store the token and show success
      localStorage.setItem('auth_token', data.token);
      setSuccess(true);

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 2000);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <PageTransition>
        <Box
          sx={{
            minHeight: 'calc(100vh - 64px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CircularProgress />
        </Box>
      </PageTransition>
    );
  }

  if (success) {
    return (
      <PageTransition>
        <Box
          sx={{
            minHeight: 'calc(100vh - 64px)',
            py: 8,
            display: 'flex',
            alignItems: 'center',
            background: `radial-gradient(circle at 30% 40%, ${alpha(theme.palette.success.main, 0.15)} 0%, transparent 70%)`
          }}
        >
          <Container maxWidth="sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 6,
                  borderRadius: 3,
                  textAlign: 'center',
                  background: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`
                }}
              >
                <CheckCircleIcon
                  sx={{
                    fontSize: 80,
                    color: theme.palette.success.main,
                    mb: 2
                  }}
                />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                  Setup Complete!
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Your admin account has been created. Redirecting to dashboard...
                </Typography>
              </Paper>
            </motion.div>
          </Container>
        </Box>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <Box
        sx={{
          minHeight: 'calc(100vh - 64px)',
          py: 8,
          display: 'flex',
          alignItems: 'center',
          background: `radial-gradient(circle at 30% 40%, ${alpha(theme.palette.primary.main, 0.15)} 0%, transparent 70%),
                       radial-gradient(circle at 80% 10%, ${alpha(theme.palette.secondary.main, 0.1)} 0%, transparent 50%)`
        }}
      >
        <Container maxWidth="sm">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                background: theme.palette.background.paper,
                backdropFilter: 'blur(10px)',
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
              }}
            >
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <AdminPanelSettingsIcon
                  sx={{
                    fontSize: 48,
                    color: theme.palette.primary.main,
                    mb: 2
                  }}
                />
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    color: theme.palette.primary.main
                  }}
                >
                  Ersteinrichtung
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                >
                  Erstelle dein erstes Administratorkonto
                </Typography>
              </Box>

              {!hasValidToken && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  Kein gültiges Setup-Token gefunden. Bitte prüfe die Server-Logs
                  für das Setup-Token oder starte den Server neu.
                </Alert>
              )}

              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                    color: theme.palette.error.main,
                    border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
                  }}
                >
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="setupToken"
                  label="Setup-Token"
                  name="setupToken"
                  value={setupToken}
                  onChange={(e) => setSetupToken(e.target.value)}
                  helperText="Das Token wurde in den Server-Logs angezeigt"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: theme.palette.text.secondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="Benutzername"
                  name="username"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: theme.palette.text.secondary }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="E-Mail"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: theme.palette.text.secondary }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Passwort"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  helperText="Mindestens 8 Zeichen"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: theme.palette.text.secondary }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Passwort bestätigen"
                  type={showPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: theme.palette.text.secondary }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    mt: 4,
                    mb: 2,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1rem'
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Administrator erstellen'
                  )}
                </Button>
              </Box>
            </Paper>
          </motion.div>
        </Container>
      </Box>
    </PageTransition>
  );
};

export default SetupPage;
